import { useMemo, useState, type FormEvent } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { CheckCircle2, Download, FileSpreadsheet, Plus, RefreshCw, Upload, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Topbar } from '@/components/layout/Topbar';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import { exportToXLSX, buildExportFilename } from '@/utils/exports';
import { reduirePhoto } from '@/api/mainOeuvre';
import { arrieresApi, LIBELLE_MODE_REGLEMENT, type ModeReglement, type PersonneArrieres } from '@/api/arrieres';

/**
 * Arriérés de salaire (« PLAN VALIDÉ ARRIÉRÉS — 1 tableau rempli, 2 RH et DAF, 3 plus anciens ») : ce qui reste dû à
 * chaque personne, ses règlements (saisis par la RH ou le DAF, validés par le DAF) et son relevé PDF.
 */
const f = (n: number) => new Intl.NumberFormat('fr-FR').format(n);
const libelleMois = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const PDF_MAX = 3 * 1024 * 1024;
const lireEnBase64 = (fichier: File) => new Promise<string>((ok, ko) => { const l = new FileReader(); l.onload = () => ok(String(l.result)); l.onerror = () => ko(l.error); l.readAsDataURL(fichier); });

export const ArrieresPage = () => {
  const role = useAuthStore((s) => s.user?.role);
  const filiale = useAuthStore((s) => s.filiale);
  const peutSaisir = role === 'rh' || role === 'daf' || role === 'admin_holding';
  const estDaf = role === 'daf' || role === 'admin_holding';
  const q = useQuery({ queryKey: ['arrieres'], queryFn: arrieresApi.registre });
  const [filtre, setFiltre] = useState('');
  const [choisi, setChoisi] = useState<string | null>(null);
  const personnes = useMemo(() => (q.data?.personnes ?? []).filter((p) => !filtre || p.beneficiaire.toLowerCase().includes(filtre.toLowerCase())), [q.data, filtre]);
  const t = q.data?.totaux;

  const exporter = () => void exportToXLSX<PersonneArrieres>({
    filename: buildExportFilename('Arrieres-de-salaire', filiale?.filialeKey, 'xlsx'), sheetName: 'Arriérés', rows: q.data?.personnes ?? [],
    columns: [
      { header: 'Bénéficiaire', accessor: (p) => p.beneficiaire }, { header: 'Situation', accessor: (p) => p.situation ?? '' },
      { header: 'Dû (F)', accessor: (p) => p.duFcfa, align: 'right' }, { header: 'Payé (F)', accessor: (p) => p.regleFcfa, align: 'right' },
      { header: 'Reste (F)', accessor: (p) => p.resteFcfa, align: 'right' }, { header: 'En attente de validation (F)', accessor: (p) => p.enAttenteFcfa, align: 'right' },
      { header: 'Mois non soldés', accessor: (p) => p.moisOuverts.join(', ') },
    ],
  });

  return (
    <>
      <Topbar title="Arriérés de salaire" subtitle="Salaires nets dus et non payés, règlements et relevés"
        actions={<button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void q.refetch()} aria-label="Rafraîchir"><RefreshCw size={14} /></button>} />
      <div className="ds-content" style={{ display: 'grid', gap: 16 }}>
        {t && (
          <div className="ds-kpi-strip cols-4">
            {[['Personnes', String(t.personnes)], ['Total dû', `${f(t.duFcfa)} F`], ['Déjà payé', `${f(t.regleFcfa)} F`], ['Reste à payer', `${f(t.resteFcfa)} F`]].map(([l, v]) => (
              <div key={l} className="ds-ks"><div className="ds-ks-label">{l}</div><div className="ds-ks-value" style={{ fontSize: 15 }}>{v}</div></div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input className="ds-fi" style={{ maxWidth: 280 }} placeholder="Rechercher un nom" value={filtre} onChange={(e) => setFiltre(e.target.value)} />
          <span style={{ flex: 1 }} />
          <button className="ds-btn ds-btn-g ds-btn-sm" disabled={!q.data?.personnes.length} onClick={exporter}><FileSpreadsheet size={14} /> Excel</button>
        </div>
        <div className="ds-table-panel">
          <div style={{ overflowX: 'auto' }}>
            <table className="ds-tbl" style={{ minWidth: 720 }}>
              <thead><tr><th>Bénéficiaire</th><th>Situation</th><th style={{ textAlign: 'right' }}>Dû</th><th style={{ textAlign: 'right' }}>Payé</th><th style={{ textAlign: 'right' }}>Reste</th><th>Mois non soldés</th></tr></thead>
              <tbody>
                {q.isLoading && <tr><td colSpan={6}>Chargement…</td></tr>}
                {!q.isLoading && !personnes.length && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucun arriéré enregistré.</td></tr>}
                {personnes.map((p) => (
                  <tr key={p.beneficiaire} onClick={() => setChoisi(choisi === p.beneficiaire ? null : p.beneficiaire)} style={{ cursor: 'pointer', background: choisi === p.beneficiaire ? 'var(--bg-2, #f5f5f5)' : undefined }}>
                    <td><strong>{p.beneficiaire}</strong>{p.enAttenteFcfa > 0 && <div style={{ fontSize: 11, color: '#92400E' }}>{f(p.enAttenteFcfa)} F en attente de validation</div>}</td>
                    <td style={{ fontSize: 12 }}>{p.situation ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{f(p.duFcfa)}</td><td style={{ textAlign: 'right' }}>{f(p.regleFcfa)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{f(p.resteFcfa)}</td>
                    <td style={{ fontSize: 12 }}>{p.moisOuverts.length ? `${p.moisOuverts.length} (${libelleMois(p.moisOuverts[0])} → ${libelleMois(p.moisOuverts[p.moisOuverts.length - 1])})` : 'soldé'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {choisi && <DetailPersonne nom={choisi} peutSaisir={peutSaisir} estDaf={estDaf} onFermer={() => setChoisi(null)} />}
      </div>
    </>
  );
};

const DetailPersonne = ({ nom, peutSaisir, estDaf, onFermer }: { nom: string; peutSaisir: boolean; estDaf: boolean; onFermer: () => void }) => {
  const qc = useQueryClient();
  const cle = ['arrieres', 'personne', nom];
  const d = useQuery({ queryKey: cle, queryFn: () => arrieresApi.detail(nom) });
  const rafraichir = () => { void qc.invalidateQueries({ queryKey: ['arrieres'] }); };
  const [form, setForm] = useState({ date: aujourdhui(), montant: '', mode: 'virement' as ModeReglement, reference: '' });
  const [preuve, setPreuve] = useState<{ url: string; nom: string } | null>(null);
  const [depot, setDepot] = useState(false);
  const saisir = useMutation({
    mutationFn: () => arrieresApi.saisirReglement({ beneficiaire: nom, date: form.date, montantFcfa: Number(form.montant), mode: form.mode, reference: form.reference || undefined, preuveUrl: preuve?.url }),
    onSuccess: () => { toast.success('Règlement enregistré', 'À faire valider par le DAF'); setForm({ ...form, montant: '', reference: '' }); setPreuve(null); rafraichir(); },
  });
  const valider = useMutation({ mutationFn: (id: string) => arrieresApi.validerReglement(id), onSuccess: () => { toast.success('Règlement validé', 'Écriture passée au journal'); rafraichir(); } });
  const annuler = useMutation({ mutationFn: ({ id, motif }: { id: string; motif: string }) => arrieresApi.annulerReglement(id, motif), onSuccess: () => { toast.success('Règlement annulé'); rafraichir(); } });
  const deposer = async (fichier: File | undefined) => {
    if (!fichier) return;
    setDepot(true);
    try {
      const pdf = fichier.type === 'application/pdf' || /\.pdf$/i.test(fichier.name);
      if (pdf && fichier.size > PDF_MAX) { toast.error('PDF trop lourd', 'Photographiez plutôt le reçu.'); return; }
      const { dataBase64, contentType } = pdf ? { dataBase64: await lireEnBase64(fichier), contentType: 'application/pdf' } : await reduirePhoto(fichier);
      setPreuve({ url: (await arrieresApi.deposerPiece({ dataBase64, contentType, nomOrigine: fichier.name })).url, nom: fichier.name });
    } catch { toast.error('Reçu non déposé', 'Réessayez.'); } finally { setDepot(false); }
  };
  const soumettre = (e: FormEvent) => { e.preventDefault(); if (Number(form.montant) > 0) saisir.mutate(); };
  const x = d.data;

  return (
    <div className="ds-table-panel">
      <div className="ds-table-header">
        <div><div className="ds-table-title">{nom}</div><div className="ds-table-sub">Reste dû : <strong>{x ? `${f(x.resteFcfa)} F` : '…'}</strong></div></div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void arrieresApi.telechargerReleve(nom).catch(() => toast.error('Relevé indisponible', 'Réessayez.'))}><Download size={13} /> Relevé PDF</button>
          <button className="ds-btn ds-btn-g ds-btn-sm" aria-label="Fermer" onClick={onFermer}><X size={13} /></button>
        </div>
      </div>
      {x && (
        <div style={{ padding: 16, display: 'grid', gap: 16 }}>
          <table className="ds-tbl" style={{ maxWidth: 640 }}>
            <thead><tr><th>Mois</th><th style={{ textAlign: 'right' }}>Dû</th><th style={{ textAlign: 'right' }}>Payé</th><th style={{ textAlign: 'right' }}>Reste</th><th>Situation</th></tr></thead>
            <tbody>{x.mois.map((m) => (
              <tr key={m.id}><td style={{ textTransform: 'capitalize' }}>{libelleMois(m.mois)}</td><td style={{ textAlign: 'right' }}>{f(m.duFcfa)}</td><td style={{ textAlign: 'right' }}>{f(m.regleFcfa)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{f(m.resteFcfa)}</td><td style={{ fontSize: 12 }}>{m.situation ?? ''}</td></tr>
            ))}</tbody>
          </table>

          <div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Règlements</div>
            {!x.reglements.length ? <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>Aucun règlement.</div> : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
                {x.reglements.map((r) => (
                  <li key={r.id} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', opacity: r.statut === 'annule' ? 0.55 : 1 }}>
                    <span>{r.date.split('-').reverse().join('/')} · <strong>{f(r.montantFcfa)} F</strong> · {LIBELLE_MODE_REGLEMENT[r.mode]}{r.reference ? ` ${r.reference}` : ''}</span>
                    {r.statut === 'valide' && <span className="ds-bd ds-bd-s">validé {r.piece ?? ''}</span>}
                    {r.statut === 'brouillon' && <span className="ds-bd ds-bd-w">en attente du DAF</span>}
                    {r.statut === 'annule' && <span className="ds-bd ds-bd-n">annulé — {r.motifAnnulation}</span>}
                    {r.preuveUrl && <a href={r.preuveUrl} target="_blank" rel="noreferrer">reçu</a>}
                    {r.statut === 'valide' && r.mois.length > 0 && <span style={{ color: 'var(--tx-3)' }}>({r.mois.map((m) => `${libelleMois(m.mois)} ${f(m.montantFcfa)}`).join(', ')})</span>}
                    {r.statut === 'brouillon' && estDaf && <button className="ds-btn ds-btn-p ds-btn-sm" disabled={valider.isPending}
                      onClick={async () => { if (await demander.confirmer({ titre: `Valider ce règlement de ${f(r.montantFcfa)} F ?`, message: 'Il soldera les mois les plus anciens et passera l’écriture au journal.', confirmer: 'Valider', danger: true })) valider.mutate(r.id); }}><CheckCircle2 size={12} /> Valider</button>}
                    {r.statut === 'brouillon' && peutSaisir && <button className="ds-btn ds-btn-g ds-btn-sm"
                      onClick={async () => { const m = await demander.texte({ titre: 'Annuler ce règlement', libelle: 'Motif de l’annulation', min: 5, confirmer: 'Annuler le règlement', danger: true }); if (m !== null) annuler.mutate({ id: r.id, motif: m }); }}><X size={12} /> Annuler</button>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {peutSaisir && x.resteFcfa > 0 && (
            <form onSubmit={soumettre} style={{ display: 'grid', gap: 6, maxWidth: 520, fontSize: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Nouveau règlement</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="ds-fi" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                <input className="ds-fi" inputMode="numeric" placeholder="Montant (F)" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value.replace(/\D/g, '') })} required />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <select className="ds-fi" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value as ModeReglement })}>
                  {(Object.keys(LIBELLE_MODE_REGLEMENT) as ModeReglement[]).map((m) => <option key={m} value={m}>{LIBELLE_MODE_REGLEMENT[m]}</option>)}
                </select>
                <input className="ds-fi" placeholder="Référence (n° de virement, de reçu…)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </div>
              <label className="ds-btn ds-btn-g ds-btn-sm" style={{ justifyContent: 'center', cursor: 'pointer' }}>
                <Upload size={12} /> {depot ? 'Dépôt…' : preuve ? `Reçu : ${preuve.nom}` : `Reçu signé (photo ou PDF)${form.mode === 'especes' ? ' — obligatoire en espèces' : ''}`}
                <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={(e) => void deposer(e.target.files?.[0])} />
              </label>
              <button type="submit" className="ds-btn ds-btn-p ds-btn-sm" disabled={saisir.isPending || depot || (form.mode === 'especes' && !preuve)} style={{ justifyContent: 'center' }}>
                <Plus size={12} /> Enregistrer le règlement
              </button>
              <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>Le règlement solde les mois les plus anciens d’abord, une fois validé par le DAF.</div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};
